/**
 * Client Deposit Page
 *
 * Mobile-first page for making global deposits (abonos) to a client's account.
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, DollarSign, CreditCard, Building, Banknote, Receipt, FileText, Smartphone, FileCheck, Clock } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDate } from '@/lib/utils'
import { useClient } from '@/hooks/useClients'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useIsMobile } from '@/hooks/useIsMobile'

interface AccountSummary {
  total_debt: number
  available_credit: number
  credit_limit: number
  pending_bills_count: number
  available_balance: number
}

interface PendingBill {
  id: number
  bill_number: string | null
  total: number
  total_deposited: number
  remaining: number
  created_at: string
}

interface DraftBill {
  id: number
  bill_number: string | null
  total: number
  created_at: string
}

interface AppliedBill {
  bill_id: number
  bill_number: string | null
  bill_total: number
  amount_applied: number
  remaining_after: number
  created_at: string
}

interface DepositResult {
  id: number
  amount: number
  applied_bills: AppliedBill[]
  new_total_debt: number
  is_fully_applied: boolean
}

const paymentTypes = [
  { value: 'cash', labelKey: 'billing.cash', icon: Banknote },
  { value: 'card', labelKey: 'billing.card', icon: CreditCard },
  { value: 'transfer', labelKey: 'billing.transfer', icon: Building },
  { value: 'sinpe', labelKey: 'payment.sinpe', icon: Smartphone },
  { value: 'check', labelKey: 'payment.check', icon: FileCheck },
]

export function ClientDepositPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // En el celular el autofoco abre el teclado y tapa la mitad de la pantalla
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const clientId = id && !isNaN(Number(id)) ? Number(id) : 0

  const [amount, setAmount] = useState('')
  const [paymentType, setPaymentType] = useState('cash')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [amountError, setAmountError] = useState('')
  const [showPendingDrafts, setShowPendingDrafts] = useState(true)

  const { data: client, isLoading: isLoadingClient } = useClient(clientId)

  // Fetch account summary
  const { data: summary, isLoading: isLoadingSummary } = useQuery<AccountSummary>({
    queryKey: ['client-account-summary', clientId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/account-summary`)
      return response.data
    },
    enabled: !!clientId,
  })

  // Fetch pending bills
  const { data: pendingBills, isLoading: isLoadingBills } = useQuery<PendingBill[]>({
    queryKey: ['client-pending-bills', clientId],
    queryFn: async () => {
      const response = await api.get(`/clients/${clientId}/pending-bills`)
      return response.data
    },
    enabled: !!clientId,
  })

  // Fetch unfinished (pending / "continue later") bills — informational only,
  // these do NOT count toward the client's debt.
  const { data: draftBills, isLoading: isLoadingDrafts } = useQuery<DraftBill[]>({
    queryKey: ['client-draft-bills', clientId],
    queryFn: async () => {
      const response = await api.get('/bills', {
        params: { client_id: clientId, status_filter: 'pending', page_size: 100 },
      })
      return response.data.items as DraftBill[]
    },
    enabled: !!clientId,
  })

  // Create deposit mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/clients/${clientId}/deposits`, {
        amount: amount,
        payment_type: paymentType,
        reference: reference || null,
        notes: notes || null,
      })
      return response.data as DepositResult
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      queryClient.invalidateQueries({ queryKey: ['client-account-summary', clientId] })
      queryClient.invalidateQueries({ queryKey: ['client-pending-bills', clientId] })
      queryClient.invalidateQueries({ queryKey: ['client-deposits', clientId] })
      queryClient.invalidateQueries({ queryKey: ['reports-credits'] })
      // Redirect to the deposit detail/result page (has a print action).
      navigate(`/clients/${clientId}/deposits/${data.id}`)
    },
  })

  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) {
      navigate(-1)
    } else {
      navigate(`/clients/${clientId}`)
    }
  }

  const totalDebt = Math.round((summary?.total_debt || 0) * 100) / 100
  const parsedAmount = parseFloat(amount) || 0
  const afterDeposit = Math.round(Math.max(0, totalDebt - parsedAmount) * 100) / 100

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!amount || parsedAmount <= 0) return
    if (totalDebt > 0 && parsedAmount > totalDebt) {
      setAmountError(`${t('validation.amountCannotExceedDebt')} (${formatCurrency(totalDebt)})`)
      return
    }
    setAmountError('')
    createMutation.mutate()
  }

  const isLoading = isLoadingClient || isLoadingSummary

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <DollarSign className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('clients.clientNotFound')}</p>
        <Button variant="outline" onClick={() => navigate('/clients')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('clients.backToClients')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={goBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">
            {t('deposits.globalDeposit')}
          </h1>
          <p className="text-sm text-muted-foreground truncate">{client.name}</p>
        </div>
      </div>

      {/* Account Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            {t('deposits.accountSummary')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30">
              <p className="text-xs text-muted-foreground mb-1">{t('deposits.totalDebt')}</p>
              <p className="text-xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(summary?.total_debt || 0)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30">
              <p className="text-xs text-muted-foreground mb-1">{t('deposits.availableCredit')}</p>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(summary?.available_credit || 0)}
              </p>
            </div>
          </div>
          {summary && summary.pending_bills_count > 0 && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              {summary.pending_bills_count} {t('deposits.pendingBillsCount')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Unfinished bills (continue-later / pending) — informational, do NOT add to debt */}
      {!isLoadingDrafts && draftBills && draftBills.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-900/40">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                {t('deposits.pendingDrafts')} ({draftBills.length})
              </CardTitle>
              <div className="flex items-center gap-2 shrink-0">
                <Switch
                  id="show-pending-drafts"
                  checked={showPendingDrafts}
                  onCheckedChange={setShowPendingDrafts}
                />
                <Label htmlFor="show-pending-drafts" className="text-xs text-muted-foreground cursor-pointer">
                  {t('deposits.showPendingDrafts')}
                </Label>
              </div>
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              {t('deposits.pendingDraftHint')}
            </p>
          </CardHeader>
          {showPendingDrafts && (
            <CardContent>
              <div className="space-y-2">
                {draftBills.map((bill) => (
                  <Link
                    key={bill.id}
                    to={`/bills/${bill.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-amber-50/60 dark:bg-amber-950/20 hover:bg-amber-100/60 dark:hover:bg-amber-950/30 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{bill.bill_number || `#${bill.id}`}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(bill.created_at)}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-amber-700 dark:text-amber-400 shrink-0">
                      {formatCurrency(bill.total)}
                    </p>
                  </Link>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* No debt message */}
      {!isLoadingSummary && totalDebt <= 0 && (
        <Card className="border-green-200 dark:border-green-900/40 bg-green-50/50 dark:bg-green-950/20">
          <CardContent className="pt-6 text-center">
            <p className="text-lg font-medium text-green-600 dark:text-green-400 mb-2">{t('deposits.noDebtMessage')}</p>
            <Button variant="outline" onClick={() => navigate(`/clients/${clientId}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('deposits.backToClient')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Deposit Form */}
      {totalDebt > 0 && <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('deposits.newDeposit')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">{t('deposits.amount')} *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  ₡
                </span>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={totalDebt > 0 ? totalDebt : undefined}
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value)
                    setAmountError('')
                  }}
                  placeholder="0"
                  inputMode="decimal"
                  className="pl-8 h-14 text-2xl font-bold"
                  required
                  autoFocus={!isMobile}
                />
              </div>
              {amountError && (
                <p className="text-sm text-red-500 dark:text-red-400">{amountError}</p>
              )}
              {totalDebt > 0 && parsedAmount > 0 && !amountError && (
                <div className="flex justify-between items-center p-2 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/30">
                  <span className="text-sm text-muted-foreground">{t('bills.afterDeposit')}</span>
                  <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(afterDeposit)}</span>
                </div>
              )}
            </div>

            {/* Payment Type */}
            <div className="space-y-2">
              <Label>{t('deposits.paymentType')}</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {paymentTypes.map((type) => {
                  const Icon = type.icon
                  return (
                    <Button
                      key={type.value}
                      type="button"
                      variant={paymentType === type.value ? 'default' : 'outline'}
                      className="h-14 flex flex-col items-center gap-1"
                      onClick={() => {
                        setPaymentType(type.value)
                        if (type.value === 'cash') setReference('')
                      }}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-xs">{t(type.labelKey)}</span>
                    </Button>
                  )
                })}
              </div>
            </div>

            {/* Reference - only for non-cash payments */}
            {paymentType !== 'cash' && (
              <div className="space-y-2">
                <Label htmlFor="reference">{t('deposits.reference')}</Label>
                <Input
                  id="reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={t('deposits.referencePlaceholder')}
                  className="h-12"
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('deposits.notes')}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('deposits.notesPlaceholder')}
                className="resize-none"
                rows={2}
              />
            </div>

          </CardContent>
        </Card>

        {/* Pending Bills */}
        {!isLoadingBills && pendingBills && pendingBills.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                {t('deposits.pendingBills')}
              </CardTitle>
              <p className="text-xs text-muted-foreground pt-1">
                {t('deposits.appliedOldestFirst')}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingBills.map((bill) => (
                  <div
                    key={bill.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div>
                      <p className="font-medium">{bill.bill_number || `#${bill.id}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(bill.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground line-through">
                        {formatCurrency(bill.total)}
                      </p>
                      <p className="font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(bill.remaining)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-12 sm:flex-1"
            onClick={goBack}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            className="h-12 sm:flex-1"
            disabled={createMutation.isPending || !amount || parseFloat(amount) <= 0}
          >
            {createMutation.isPending && <Spinner size="sm" className="mr-2" />}
            {createMutation.isPending ? t('deposits.creating') : t('deposits.createDeposit')}
          </Button>
        </div>
      </form>}
    </div>
  )
}
